import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Coins, Plus, Zap, TrendingDown, TrendingUp, Settings, ArrowDownCircle, ArrowUpCircle, Sparkles } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WalletPanel = () => {
  const { token } = useAuth();
  const [balance, setBalance] = useState(null);
  const [packages, setPackages] = useState({});
  const [costs, setCosts] = useState({});
  const [history, setHistory] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTopup, setShowTopup] = useState(false);
  const [showAutoTopup, setShowAutoTopup] = useState(false);
  const [autoSettings, setAutoSettings] = useState({ enabled: false, threshold: 200, topup_package: 'small' });
  const [purchasing, setPurchasing] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [bal, pkg, cost, hist, purch] = await Promise.all([
        axios.get(`${API_URL}/api/wallet/balance`, authHeaders),
        axios.get(`${API_URL}/api/wallet/topup-packages`),
        axios.get(`${API_URL}/api/wallet/credit-costs`),
        axios.get(`${API_URL}/api/wallet/history`, authHeaders),
        axios.get(`${API_URL}/api/wallet/purchases`, authHeaders).catch(() => ({ data: [] }))
      ]);
      setBalance(bal.data);
      setPackages(pkg.data);
      setCosts(cost.data);
      setHistory(hist.data);
      setPurchases(purch.data);
      setAutoSettings({
        enabled: bal.data.auto_topup?.enabled || false,
        threshold: bal.data.auto_topup?.threshold || 200,
        topup_package: bal.data.auto_topup?.package || 'small'
      });
    } catch (err) {
      console.error('Wallet fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleTopup = async (pkgId) => {
    setPurchasing(pkgId);
    try {
      const res = await axios.post(`${API_URL}/api/wallet/topup`, {
        package_id: pkgId,
        origin_url: window.location.origin
      }, authHeaders);
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore nel checkout');
    } finally {
      setPurchasing(null);
    }
  };

  const saveAutoTopup = async () => {
    try {
      await axios.post(`${API_URL}/api/wallet/auto-topup`, autoSettings, authHeaders);
      setShowAutoTopup(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  if (loading) {
    return (
      <div className="py-12 text-center">
        <div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-[#A1A1AA] mt-4 text-sm">Caricamento wallet...</p>
      </div>
    );
  }

  if (!balance) return null;

  const total = balance.total || 0;
  const isLow = total < 200;

  const costLabels = {
    seo_analysis: 'Analisi SEO',
    meta_generation: 'Meta Tag',
    content_generation_short: 'Contenuto Corto (~500 parole)',
    content_generation_medium: 'Contenuto Medio (~1000 parole)',
    content_generation_long: 'Contenuto Lungo (~2000 parole)',
    local_seo: 'Local SEO Page',
    demo_analysis: 'Demo (Gratuita)'
  };

  return (
    <div className="space-y-6" data-testid="wallet-panel">
      {/* Balance Card */}
      <div className="bg-gradient-to-br from-[#0055FF]/10 to-[#6366F1]/10 rounded-2xl p-6 border border-[#0055FF]/20">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#0055FF]/20 flex items-center justify-center">
              <Coins className="w-6 h-6 text-[#0055FF]" />
            </div>
            <div>
              <span className="text-[10px] text-[#A1A1AA] uppercase tracking-wider block">Saldo Crediti</span>
              <span className={`text-4xl font-['Outfit'] font-bold ${isLow ? 'text-[#EF4444]' : 'text-white'}`} data-testid="wallet-total-credits">
                {total.toLocaleString()}
              </span>
            </div>
          </div>
          <Button
            onClick={() => setShowTopup(!showTopup)}
            className="bg-[#0055FF] hover:bg-[#3377FF] text-white rounded-full"
            data-testid="buy-credits-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Ricarica
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
          <div className="bg-black/20 rounded-xl p-3 text-center">
            <span className="text-[9px] text-[#A1A1AA] uppercase block mb-1">Piano</span>
            <span className="text-lg font-['Outfit'] font-semibold text-[#0055FF]" data-testid="wallet-plan-credits">{(balance.plan_credits || 0).toLocaleString()}</span>
          </div>
          <div className="bg-black/20 rounded-xl p-3 text-center">
            <span className="text-[9px] text-[#A1A1AA] uppercase block mb-1">Top-up</span>
            <span className="text-lg font-['Outfit'] font-semibold text-[#F59E0B]" data-testid="wallet-topup-credits">{(balance.topup_credits || 0).toLocaleString()}</span>
          </div>
          <div className="bg-black/20 rounded-xl p-3 text-center">
            <span className="text-[9px] text-[#A1A1AA] uppercase block mb-1">Addebitati</span>
            <span className="text-lg font-['Outfit'] font-semibold text-[#A1A1AA]" data-testid="wallet-total-charged">{(balance.total_spent || 0).toLocaleString()}</span>
          </div>
          <div className="bg-black/20 rounded-xl p-3 text-center">
            <span className="text-[9px] text-[#A1A1AA] uppercase block mb-1">Consumati</span>
            <span className="text-lg font-['Outfit'] font-semibold text-[#A1A1AA]" data-testid="wallet-total-consumed">{(typeof balance.total_consumed === 'number' ? balance.total_consumed : Math.max(0, (balance.total_spent || 0) - (balance.total_refunded || 0))).toLocaleString()}</span>
          </div>
        </div>

        {isLow && (
          <div className="mt-4 p-3 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-xl flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-[#EF4444]" />
            <span className="text-xs text-[#EF4444]">Crediti in esaurimento! Ricarica per continuare ad usare gli agenti AI.</span>
          </div>
        )}
      </div>

      {/* Top-up Packages */}
      {showTopup && (
        <div className="space-y-3" data-testid="topup-packages">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#F59E0B]" /> Pacchetti Ricarica
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(packages).map(([id, pkg]) => (
              <div
                key={id}
                className={`bg-[#0a0a0a] rounded-xl p-4 border transition-colors hover:border-[#0055FF]/30 ${
                  id === 'medium' ? 'border-[#F59E0B]/30 ring-1 ring-[#F59E0B]/20' : 'border-white/5'
                }`}
                data-testid={`topup-package-${id}`}
              >
                {id === 'medium' && (
                  <div className="text-[8px] text-[#F59E0B] uppercase tracking-widest mb-2 font-bold">Piu' popolare</div>
                )}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{pkg.name}</span>
                  <span className="text-xs text-[#A1A1AA]">€{pkg.price_per_credit}/cr</span>
                </div>
                <div className="flex items-baseline gap-1 mb-3">
                  <span className="text-2xl font-['Outfit'] font-bold">{pkg.credits.toLocaleString()}</span>
                  <span className="text-xs text-[#A1A1AA]">crediti</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-['Outfit'] font-semibold text-[#0055FF]">€{pkg.price}</span>
                  <Button
                    onClick={() => handleTopup(id)}
                    disabled={purchasing === id}
                    size="sm"
                    className="bg-[#0055FF] hover:bg-[#3377FF] text-white text-xs rounded-full"
                    data-testid={`buy-topup-${id}`}
                  >
                    {purchasing === id ? (
                      <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>Acquista</>
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Auto Top-up Settings */}
      <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-[#A1A1AA]" />
            <span className="text-sm font-medium">Auto Top-up</span>
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
              autoSettings.enabled ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-white/5 text-[#A1A1AA]'
            }`}>{autoSettings.enabled ? 'Attivo' : 'Disattivo'}</span>
          </div>
          <button
            onClick={() => setShowAutoTopup(!showAutoTopup)}
            className="text-xs text-[#0055FF] hover:text-[#3377FF]"
            data-testid="toggle-auto-topup-settings"
          >
            Configura
          </button>
        </div>

        {showAutoTopup && (
          <div className="mt-4 space-y-3 pt-4 border-t border-white/5">
            <div className="flex items-center gap-3">
              <label className="text-xs text-[#A1A1AA] w-24">Abilitato</label>
              <button
                onClick={() => setAutoSettings(p => ({ ...p, enabled: !p.enabled }))}
                className={`w-10 h-5 rounded-full transition-colors relative ${autoSettings.enabled ? 'bg-[#10B981]' : 'bg-white/10'}`}
                data-testid="auto-topup-toggle"
              >
                <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform ${autoSettings.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-xs text-[#A1A1AA] w-24">Soglia</label>
              <select
                value={autoSettings.threshold}
                onChange={e => setAutoSettings(p => ({ ...p, threshold: parseInt(e.target.value) }))}
                className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white"
                data-testid="auto-topup-threshold"
              >
                <option value={100}>100 crediti</option>
                <option value={200}>200 crediti</option>
                <option value={500}>500 crediti</option>
                <option value={1000}>1000 crediti</option>
              </select>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-xs text-[#A1A1AA] w-24">Pacchetto</label>
              <select
                value={autoSettings.topup_package}
                onChange={e => setAutoSettings(p => ({ ...p, topup_package: e.target.value }))}
                className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white"
                data-testid="auto-topup-package"
              >
                {Object.entries(packages).map(([id, pkg]) => (
                  <option key={id} value={id}>{pkg.name} - {pkg.credits} cr (€{pkg.price})</option>
                ))}
              </select>
            </div>
            <Button onClick={saveAutoTopup} size="sm" className="bg-[#10B981] hover:bg-[#059669] text-white text-xs" data-testid="save-auto-topup-btn">
              Salva
            </Button>
          </div>
        )}
      </div>

      {/* Credit Costs Table */}
      <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
        <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-3 flex items-center gap-2">
          <Zap className="w-3 h-3" /> Costo per Operazione
        </h4>
        <div className="space-y-1.5">
          {Object.entries(costs).map(([key, cost]) => (
            <div key={key} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
              <span className="text-xs text-white">{costLabels[key] || key}</span>
              <span className={`text-xs font-mono font-medium ${cost === 0 ? 'text-[#10B981]' : 'text-[#F59E0B]'}`}>
                {cost === 0 ? 'Gratis' : `${cost} crediti`}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Purchase History Table */}
      <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5" data-testid="purchase-history">
        <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-3 flex items-center gap-2">
          <ArrowUpCircle className="w-3 h-3" /> Storico Acquisti
        </h4>
        {purchases.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/10 text-[#A1A1AA]">
                  <th className="text-left py-2 pr-3 font-medium">Data</th>
                  <th className="text-left py-2 pr-3 font-medium">Tipo</th>
                  <th className="text-right py-2 pr-3 font-medium">Crediti</th>
                  <th className="text-right py-2 pr-3 font-medium">Importo</th>
                  <th className="text-center py-2 font-medium">Stato</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((p, i) => (
                  <tr key={p.id || i} className="border-b border-white/5 last:border-0">
                    <td className="py-2 pr-3 text-[#A1A1AA]">{(p.created_at || '').substring(0, 10)}</td>
                    <td className="py-2 pr-3 text-white">{p.metadata?.package_name || p.package_id || '-'}</td>
                    <td className="py-2 pr-3 text-right font-mono text-[#10B981]">
                      {p.metadata?.credits ? (+p.metadata.credits).toLocaleString() : '-'}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono text-white">
                      {p.amount != null ? `${p.amount.toFixed(2)} ${(p.currency || 'eur').toUpperCase()}` : '-'}
                    </td>
                    <td className="py-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                        p.status === 'completed' || p.payment_status === 'paid' ? 'bg-[#10B981]/20 text-[#10B981]' :
                        p.status === 'pending' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                        'bg-[#EF4444]/20 text-[#EF4444]'
                      }`}>
                        {p.status === 'completed' || p.payment_status === 'paid' ? 'Completato' :
                         p.status === 'pending' ? 'In attesa' : p.status || '-'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-xs text-[#A1A1AA] text-center py-4">Nessun acquisto ancora</p>
        )}
      </div>

      {/* Transaction History */}
      <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
        <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-3">Storico Crediti</h4>
        {history.length > 0 ? (
          <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
            {history.map((tx, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                  tx.type === 'credit' ? 'bg-[#10B981]/20' : 'bg-[#EF4444]/20'
                }`}>
                  {tx.type === 'credit' ? (
                    <ArrowUpCircle className="w-3.5 h-3.5 text-[#10B981]" />
                  ) : (
                    <ArrowDownCircle className="w-3.5 h-3.5 text-[#EF4444]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs block truncate">
                    {tx.type === 'credit' ? `Ricarica: ${tx.source || 'Piano'}` : `${costLabels[tx.operation] || tx.operation}`}
                  </span>
                  <span className="text-[9px] text-[#A1A1AA]">{tx.timestamp?.substring(0, 16).replace('T', ' ')}</span>
                </div>
                <div className="text-right">
                  <span className={`text-xs font-mono font-medium ${tx.amount > 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount}
                  </span>
                  <span className="text-[9px] text-[#A1A1AA] block">= {tx.balance_after}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-[#A1A1AA] text-center py-4">Nessuna transazione</p>
        )}
      </div>
    </div>
  );
};

export default WalletPanel;
